// HumanResourceDashboard.js

import React, { useEffect, useState } from "react";

function HumanResourceDashboard({ dashboardData }) {
  const [animated, setAnimated] = useState(false);
  const [hoveredChart, setHoveredChart] = useState(null);

  // Derive HR Data from dashboardData prop
  const hrData = {
    totalStaff:
      dashboardData?.hrmsStatistics?.reduce(
        (acc, curr) => acc + parseInt(curr.employeeCount || 0),
        0
      ) ||
      dashboardData?.summaryStatistics?.[0]?.staffCount ||
      0,
    openPositions: dashboardData?.summaryStatistics?.[0]?.openTicketCount || 0,
    employeesByDepartment: {
      labels:
        dashboardData?.hrmsStatistics?.map((s) => s.departmentName) || [
          "Teaching",
          "Admin",
          "Accounts",
          "Support",
          "Transport",
          "IT",
        ],
      data: dashboardData?.hrmsStatistics?.map((s) =>
        parseInt(s.employeeCount || 0)
      ) || [45, 12, 8, 15, 10, 7],
      colors: [
        "#9b59b6",
        "#8e44ad",
        "#7d3c98",
        "#6c3483",
        "#5b2c6f",
        "#4a235a",
        "#3498db",
        "#2ecc71",
        "#e74c3c",
        "#f39c12",
      ],
    },
  };

  useEffect(() => {
    setAnimated(true);
  }, []);

  const renderEmployeesChart = () => {
    const maxValue = Math.max(...hrData.employeesByDepartment.data);

    return (
      <div style={{ position: "relative", height: "250px" }}>
        <div
          className="chart-container"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-end",
            height: "200px",
            padding: "0 10px",
            background: "linear-gradient(180deg, #f8f9fa 0%, #ffffff 100%)",
            borderRadius: "8px",
          }}
        >
          {hrData.employeesByDepartment.data.map((value, index) => (
            <div
              key={index}
              className="bar-container"
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                flex: 1,
                margin: "0 2px",
              }}
              onMouseEnter={() => setHoveredChart({ type: "employees", index })}
              onMouseLeave={() => setHoveredChart(null)}
            >
              <div
                className="bar-value"
                style={{
                  marginBottom: "5px",
                  fontSize: "11px",
                  fontWeight: "600",
                  color: "#2c3e50",
                  opacity:
                    hoveredChart?.type === "employees" &&
                    hoveredChart?.index === index
                      ? 1
                      : 0.7,
                }}
              >
                {value}
              </div>
              <div
                className="bar-wrapper"
                style={{
                  height: "150px",
                  width: `${20 + index * 3}px`,
                  backgroundColor: "rgba(155, 89, 182, 0.1)",
                  borderRadius: "4px 4px 0 0",
                  position: "relative",
                  transition: "all 0.3s ease",
                }}
              >
                <div
                  className="bar"
                  style={{
                    height: animated ? `${(value / maxValue) * 100}%` : "0%",
                    backgroundColor: hrData.employeesByDepartment.colors[index],
                    width: "100%",
                    borderRadius: "4px 4px 0 0",
                    transition: "height 0.3s ease",
                    transform:
                      hoveredChart?.type === "employees" &&
                      hoveredChart?.index === index
                        ? "scale(1.05)"
                        : "scale(1)",
                    boxShadow:
                      hoveredChart?.type === "employees" &&
                      hoveredChart?.index === index
                        ? "0 4px 8px rgba(0,0,0,0.2)"
                        : "none",
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                  }}
                ></div>
              </div>
              <div
                className="bar-label"
                style={{
                  fontSize: "10px",
                  marginTop: "8px",
                  color: "#2c3e50",
                  fontWeight: "600",
                  textAlign: "center",
                  height: "30px",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {hrData.employeesByDepartment.labels[index]}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div>
      {/* 2 KPI Tiles */}
      <div className="row g-3 mb-4">
        <div className="col-6 col-md-3">
          <div
            className="card rounded-3 p-3 shadow-sm"
            style={{ borderLeft: "4px solid #9b59b6" }}
          >
            <div className="d-flex align-items-center">
              <div
                className="me-3"
                style={{ color: "#9b59b6", fontSize: "28px" }}
              >
                <i className="fas fa-users"></i>
              </div>
              <div>
                <div className="text-uppercase fs-8 text-muted fw-bold">
                  Total Staff
                </div>
                <div className="h4 mb-0 fw-bold" style={{ color: "#2c3e50" }}>
                  {hrData.totalStaff}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-6 col-md-3">
          <div
            className="card rounded-3 p-3 shadow-sm"
            style={{ borderLeft: "4px solid #e67e22" }}
          >
            <div className="d-flex align-items-center">
              <div
                className="me-3"
                style={{ color: "#e67e22", fontSize: "28px" }}
              >
                <i className="fas fa-briefcase"></i>
              </div>
              <div>
                <div className="text-uppercase fs-8 text-muted fw-bold">
                  Open Positions
                </div>
                <div className="h4 mb-0 fw-bold" style={{ color: "#2c3e50" }}>
                  {hrData.openPositions}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* HR Overview Section */}
      <div className="card mb-3 shadow-sm">
        <div className="card-body">
          <h6
            className="card-title fw-bold"
            style={{ color: "#2c3e50", marginBottom: "20px" }}
          >
            <i
              className="fas fa-briefcase me-2"
              style={{ color: "#e67e22" }}
            ></i>
            Employees: Headcount by Department
          </h6>
          {renderEmployeesChart()}
        </div>
      </div>
    </div>
  );
}

export default HumanResourceDashboard;
